import * as React from 'react';
import { useParams } from 'react-router-dom';
import { Panel, Spinner } from '@ixicare/ui';
import { t } from '~/i18n';
import { State } from '~/store/types';
import connect from '~/store/connect';
import { useStores } from '~store/utils';
import ErrorContainer from '~/containers/Error';
import { useBreadcrumbs } from '~components/DashboardWrapper/Breadcrumbs';
import crumbs from '~config/crumbs';
import HealthcareProviderFormContainer from '~/containers/HealthcareProviders/HealthcareProviderForm';
import { ProfileSessions } from '~/containers/HealthcareProviders/ProfileSessions';

interface HealthcareProviderUpdatePageProps {
  healthcareProvider: State['healthcareProviderDetail']['profile'];
  loadHealthcareProvider: State['healthcareProviderDetail']['loadHealthcareProvider'];
  healthcareProviderLoading: State['healthcareProviderDetail']['loading'];
  healthcareProviderError: State['healthcareProviderDetail']['error'];
}

const HealthcareProviderUpdatePage: React.FC<HealthcareProviderUpdatePageProps> = ({
  healthcareProvider,
  loadHealthcareProvider,
  healthcareProviderLoading,
  healthcareProviderError,
}) => {
  const { id } = useParams<{ id: string }>();
  const healthcareProviderId = id ? parseInt(id, 10) : 0;
  const { auth } = useStores();
  const isOwnProfile = !!auth.user && auth.user.id === healthcareProviderId;

  // Editing your own account is reached from the user menu, not the list, so it gets no trail —
  // same condition the hand-rolled back link used. There is no HCP detail route to sit between.
  useBreadcrumbs(isOwnProfile ? [] : [crumbs.healthcareProviders(), { label: t('healthcareProviders.update.title') }]);

  React.useEffect(() => {
    if (healthcareProviderLoading) {
      return;
    }
    if (healthcareProviderId && (!healthcareProvider || healthcareProvider.id !== healthcareProviderId)) {
      loadHealthcareProvider(healthcareProviderId);
    }
  }, [healthcareProviderId, healthcareProvider, healthcareProviderLoading, loadHealthcareProvider]);

  React.useEffect(() => {
    document.title = t('meta.title', {
      title: isOwnProfile ? t('healthcareProviders.editMyAccount.title') : t('healthcareProviders.update.title'),
    });
  }, [isOwnProfile]);

  if (healthcareProviderLoading) {
    return <Spinner label={t('global.loading')} />;
  }
  return (
    <>
      <h1>{isOwnProfile ? t('healthcareProviders.editMyAccount.title') : t('healthcareProviders.update.title')}</h1>
      <ErrorContainer error={healthcareProviderError} />
      {!healthcareProviderError && (
        <>
          <Panel>
            <HealthcareProviderFormContainer healthcareProvider={healthcareProvider} />
          </Panel>
          {healthcareProvider?.uuid && (
            <ProfileSessions profileUuid={healthcareProvider.uuid} isOwnProfile={isOwnProfile} />
          )}
        </>
      )}
    </>
  );
};

const ConnectedHealthcareProviderUpdatePage = connect((state: State) => ({
  healthcareProvider: state.healthcareProviderDetail.profile,
  loadHealthcareProvider: state.healthcareProviderDetail.loadHealthcareProvider,
  healthcareProviderLoading: state.healthcareProviderDetail.loading,
  healthcareProviderError: state.healthcareProviderDetail.error,
}))(HealthcareProviderUpdatePage);

export default ConnectedHealthcareProviderUpdatePage;
